/**
 * Cleaners List Page
 *
 * Operational view: filters, cleaner drawer (view-only), absence
 * management.
 *
 * The "+" action on each row opens a centered modal hosting the
 * absence create form pre-filled with the row's cleaner. Same shared
 * #absence-modal shell as the absences list / calendar pages, driven
 * by ``createDynamicModal`` (see ``form-modals.mdc`` and
 * ``frontend/src/utils/ui/dynamic-modal.js``). This replaces the legacy
 * fullpage panel + ``Modal.js`` flow that lived here before
 * 2026-05-06.
 *
 * All user management (create, edit, toggle) is handled via the Team
 * Management page. Filtering / sorting / pagination is handled
 * declaratively by HTMX.
 */

import { createDynamicModal } from '../utils/ui/dynamic-modal.js';

// Module-level helper for the shared #absence-modal. Created lazily on
// first need (after the shell is present in the DOM) and reset on
// every page-level init so a stale reference to a detached modal node
// from a previous boost cycle never sticks around — see
// ``form-modals.mdc`` §9 ("Boosted-nav module caches"). Same trick as
// ``customers.js`` / ``jobs.js`` / ``absences.js``.
let absenceModal = null;

const state = {
  urls: {},
  translations: {},
};

/**
 * Initialize the cleaners list page
 */
export function initCleanersList(options = {}) {
  const {
    translations = {},
    urls = {},
    hasActiveFilters = false,
  } = options;

  state.urls = urls;
  state.translations = translations;

  // Reset cached helper so a re-init after an hx-boost swap binds to
  // the fresh modal shell in the new DOM, not to the orphaned one
  // from the previous page life.
  absenceModal = null;

  initFilterPanelUI(hasActiveFilters);
  bindCleanersActionsOnce();
  bindAfterSettleOnce();
  maybeOpenDrawerFromQuery();
}

function initFilterPanelUI(hasActiveFilters) {
  var panel = document.getElementById('filter-panel');
  var chevron = document.getElementById('filter-chevron');
  var toggleBtn = document.getElementById('toggle-filters-btn');
  var resetBtn = document.getElementById('reset-filters-btn');
  var clearSearchBtn = document.getElementById('clear-search-btn');
  var form = document.getElementById('cleanerFilterForm');
  var searchInput = document.getElementById('search-input');

  if (panel && hasActiveFilters) {
    panel.classList.remove('hidden');
    if (chevron) chevron.classList.add('rotate-180');
  }

  // Per-node idempotency via plain property (not dataset) so the flag
  // does not serialize into HTMX hx-boost history snapshots.
  if (toggleBtn && panel && !toggleBtn._clFilterBound) {
    toggleBtn._clFilterBound = true;
    toggleBtn.addEventListener('click', function () {
      panel.classList.toggle('hidden');
      if (chevron) chevron.classList.toggle('rotate-180');
    });
  }

  if (resetBtn && form && !resetBtn._clResetBound) {
    resetBtn._clResetBound = true;
    resetBtn.addEventListener('click', function () {
      form.querySelectorAll('select').forEach(function (sel) {
        sel.value = '';
        var wrapper = sel.closest('.custom-select-wrapper');
        if (wrapper) {
          var textEl = wrapper.querySelector('.custom-select-text');
          if (textEl && sel.options[0]) {
            textEl.textContent = sel.options[0].text;
          }
        }
      });
      if (searchInput) searchInput.value = '';
      if (clearSearchBtn) clearSearchBtn.classList.add('hidden');
      form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    });
  }

  if (clearSearchBtn && searchInput && form && !clearSearchBtn._clClearBound) {
    clearSearchBtn._clClearBound = true;
    clearSearchBtn.addEventListener('click', function () {
      searchInput.value = '';
      clearSearchBtn.classList.add('hidden');
      form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    });
  }

  if (searchInput && clearSearchBtn && !searchInput._clInputBound) {
    searchInput._clInputBound = true;
    searchInput.addEventListener('input', function () {
      clearSearchBtn.classList.toggle('hidden', !searchInput.value.trim());
    });
  }
}

// ───────────────────────────────────────────────────────────────────────────
// Absence create modal (shared #absence-modal, createDynamicModal)
// ───────────────────────────────────────────────────────────────────────────
//
// Mirror of absences.js / customers.js / jobs.js: lazy-create the
// helper, plug a ``onContentInjected`` hook to re-attach Flowbite
// widgets after the GET / 422 re-render, and a ``onSuccess`` callback
// to toast the server-shipped message + soft-reload the list.

function ensureAbsenceModalHelper() {
  if (absenceModal) return absenceModal;
  absenceModal = createDynamicModal({
    modalId: 'absence-modal',
    onContentInjected: initFormWidgets,
    onSuccess: handleAbsenceSuccess,
    errorLoadingMessage:
      state.translations.errorLoading || 'Error loading form. Please try again.',
    closeButtonText: state.translations.retry || 'Retry',
  });
  return absenceModal;
}

function initFormWidgets(content) {
  // The injected <script> in _form_content.html already constructs
  // the Flowbite Datepicker on every [datepicker] input. Here we just
  // ensure custom selects + Flowbite tooltips/dropdowns get
  // re-attached on initial GET and 422 re-render.
  if (typeof window.initCustomSelects === 'function') {
    window.initCustomSelects();
  }
  if (typeof window.reinitFlowbiteWidgets === 'function') {
    window.reinitFlowbiteWidgets(content);
  }
}

function handleAbsenceSuccess(data, helper) {
  // Server ships the success message in the JSON (see ``absence_create``
  // in apps/office/views/absences.py): we toast it directly rather than
  // relying on Django's messages queue + soft-reload, because plain XHR
  // (X-Requested-With) doesn't go through ``HtmxMessagesMiddleware``.
  // See ``toast-feedback.mdc`` § anti-pattern "double-toast".
  if (data && data.message && typeof window.showToast === 'function') {
    window.showToast(
      data.message
        || state.translations.absenceCreated
        || 'Absence created!',
      data.message_type || 'success',
    );
  }
  // Defer the soft-reload via the helper's afterClose hook so the
  // close transition fully completes before the HTMX swap tears the
  // backdrop down — otherwise the user sees a "white flash" on
  // Pattern B pages where the partial comes back in <100 ms.
  helper.close(() => {
    if (typeof window._softReload === 'function') {
      window._softReload();
    }
  });
}

/**
 * Open the absence-create modal pre-filled for ``cleanerId``.
 *
 * Internal helper, used by both the row-level "+" button on the
 * cleaners list (declarative ``data-cleaners-action="open-absence"``,
 * see ``bindCleanersActionsOnce``) and any other in-page trigger
 * that wants the same modal. The cleaner is preselected by
 * appending ``?cleaner=<id>`` to the create URL; the server reads
 * that, builds ``preselected_cleaner`` and renders a read-only
 * cleaner block + hidden ``cleaner`` input in _form_content.html.
 */
function openAbsenceModal(cleanerId, cleanerName) {
  const helper = ensureAbsenceModalHelper();
  if (!helper) return;
  const baseUrl = state.urls.absenceCreate || '/office/cleaners/absences/new/';
  const sep = baseUrl.indexOf('?') === -1 ? '?' : '&';
  const url = baseUrl + sep + 'cleaner=' + encodeURIComponent(cleanerId);
  const editLabel = state.translations.addAbsence || 'Add Absence';
  const title = cleanerName ? editLabel + ': ' + cleanerName : editLabel;
  helper.open(url, title);
}

// Delegate at document level for the row-level "+ Absence" action
// button on the cleaners list. Phase 21.A migration of inline
// ``onclick="openAbsenceModal(id, name)"``. Idempotent — bound once
// per SPA lifetime via ``_clActionsBound``.
//
// Bound in the CAPTURE phase so it fires before the action cell's
// ``[data-stop-propagation]`` stops the bubble — same pattern as
// ``utils/ui/confirm-action.js`` (Phase 18 Bug 2 fix). Without this,
// the stopper meant to block the row-level ``data-show-cleaner-
// drawer`` delegate would also swallow this open-absence trigger.
let _clActionsBound = false;

function bindCleanersActionsOnce() {
  if (_clActionsBound) return;
  _clActionsBound = true;

  document.body.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-cleaners-action]');
    if (!trigger) return;
    const action = trigger.dataset.cleanersAction;
    if (action !== 'open-absence') return;
    const id = parseInt(trigger.dataset.cleanerId, 10);
    if (!Number.isFinite(id)) return;
    const name = trigger.dataset.cleanerName || '';
    openAbsenceModal(id, name);
  }, true);
}

// Module-level idempotency flags (see pages/customers.js for rationale:
// listeners on ``document``/``document.body`` must be bound ONCE for
// the SPA lifetime; hx-boost re-inits would otherwise stack them).
let afterSettleBound = false;
let openDrawerCheckedOnce = false;

function bindAfterSettleOnce() {
  if (afterSettleBound) return;
  afterSettleBound = true;
  document.addEventListener('htmx:afterSettle', function (e) {
    var target = e.detail && e.detail.target;
    if (!target || target.id !== 'list-results') return;
    if (typeof window.reinitFlowbiteWidgets === 'function') {
      window.reinitFlowbiteWidgets(target);
    }
  });
}

/**
 * Honour ``?open=<id>`` deep-link that opens the cleaner drawer
 * directly. Run ONCE per full page load — subsequent hx-boost
 * navigations don't carry the param and we don't want to re-open
 * the drawer on every autoInit call.
 */
function maybeOpenDrawerFromQuery() {
  if (openDrawerCheckedOnce) return;
  openDrawerCheckedOnce = true;
  var params = new URLSearchParams(window.location.search);
  var openId = params.get('open');
  if (!openId || typeof window.showCleanerDrawer !== 'function') return;
  window.showCleanerDrawer(parseInt(openId, 10));
  params.delete('open');
  var cleanUrl = window.location.pathname + (params.toString() ? '?' + params.toString() : '');
  history.replaceState(null, '', cleanUrl);
}

/**
 * Auto-init entry point (wired via ``data-auto-init="cleanersList"``).
 */
export function autoInitCleanersList() {
  const root = document.querySelector('[data-auto-init="cleanersList"]');
  if (!root) return;
  const ds = root.dataset;
  initCleanersList({
    hasActiveFilters: ds.hasActiveFilters === '1',
    urls: {
      absenceCreate: ds.absenceCreateUrl || '/office/cleaners/absences/new/',
      drawerUrl: ds.drawerUrl || '/office/cleaners/{id}/drawer/',
    },
    translations: {
      addAbsence: ds.trAddAbsence || 'Add Absence',
      absenceCreated: ds.trAbsenceCreated || 'Absence created!',
      errorLoading: ds.trErrorLoading || 'Error loading form. Please try again.',
      retry: ds.trRetry || 'Retry',
    },
  });
}

window.initCleanersList = initCleanersList;
